import React, { useState } from 'react';
import { Shield, AlertCircle, CheckCircle, Loader2 } from 'lucide-react';
import { updateUserRole } from '../../../utils/admin/userRoleManager';
import { useMemberStore } from '../../../stores/memberStore';

interface RoleSelectorProps {
  userId: string;
  currentRole: string;
  onClose: () => void;
}

export function RoleSelector({ userId, currentRole, onClose }: RoleSelectorProps) {
  const [isUpdating, setIsUpdating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [selectedRole, setSelectedRole] = useState<string>(currentRole || 'user');

  const {users, setMembers} = useMemberStore();

  const handleRoleChange = async (newRole: 'user' | 'admin') => {
    if (newRole === selectedRole) return;
    
    setIsUpdating(true);
    setError(null);
    setSuccess(null);
    
    try {
      await updateUserRole(userId, newRole);
      setMembers(users.map(u => u.id === userId ? { ...u, role: newRole } : u));
      setSelectedRole(newRole);
      setSuccess(`ロールを${newRole === 'admin' ? '管理者' : 'ユーザー'}に変更しました`);
      
      
    } catch (err) {
      setError(err instanceof Error ? err.message : '更新に失敗しました');
    } finally {
      setIsUpdating(false);
    }

    if (onClose) {
      setTimeout(() => {
        onClose();
      }, 1500);
    }
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2">
        <Shield className="w-4 h-4 text-gray-500" />
        <span className="text-sm font-medium text-gray-700">ユーザーロール</span>
      </div>
      
      <div className="flex items-center gap-2">
        <button
          onClick={() => handleRoleChange('user')}
          disabled={isUpdating || selectedRole === 'user'}
          className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
            selectedRole === 'user'
              ? 'bg-gray-100 text-gray-800'
              : 'text-gray-600 hover:bg-gray-50'
          }`}
        >
          ユーザー
        </button>
        
        <button
          onClick={() => handleRoleChange('admin')}
          disabled={isUpdating || selectedRole === 'admin'}
          className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
            selectedRole === 'admin'
              ? 'bg-blue-100 text-blue-800'
              : 'text-gray-600 hover:bg-gray-50'
          }`}
        >
          管理者
        </button>
        
        {isUpdating && (
          <Loader2 className="w-4 h-4 text-blue-500 animate-spin" />
        )}
      </div>
      
      {error && (
        <div className="flex items-center gap-2 text-sm text-red-600 bg-red-50 p-2 rounded">
          <AlertCircle className="w-4 h-4 flex-shrink-0" />
          <p>{error}</p>
        </div>
      )}
      
      {success && (
        <div className="flex items-center gap-2 text-sm text-green-600 bg-green-50 p-2 rounded">
          <CheckCircle className="w-4 h-4 flex-shrink-0" />
          <p>{success}</p>
        </div>
      )}
    </div>
  );
}